/**
 * Code Frequency Report — diagnose which validation codes dominate the
 * total findings count.
 *
 * Context: The admin Overview shows "X bevindingen totaal" (e.g. 5168).
 * That number is a flat sum across ~2000+ questions. Without knowing WHICH
 * codes are responsible, admins can't tell whether it's a real quality
 * issue or noise from a few over-eager validators.
 *
 * This utility walks a PipelineBatchResult and produces a code-level
 * rollup so the UI can surface "top noisemakers" and let admins decide
 * whether to dial down severity, mute, or fix the seeds.
 *
 * Purely additive — no verdict shift, no severity change. Diagnostic only.
 */

import { SEVERITY } from '../severityScale'
import type { PipelineBatchResult, ValidationIssue } from '../issues/types'
import type { IssueCode, IssueDomain, IssueKind } from '../issues/codes'

// ─── Rollup codes that carry metadata.reason as secondary discriminator ──────
// These codes are themselves the primary grouping; metadata.reason gives the
// secondary breakdown. Admin UI surfaces this as a sub-list under the code row.
const ROLLUP_CODES: ReadonlySet<string> = new Set([
  'CONTENT_CITO_ISSUE',
  'CONTENT_DISTRACTOR_QUALITY',
  'CONTENT_PLUS_INADEQUATE',
  'DIVERSITY_TYPE_OVERUSE',
  'SUPPORT_EXPLANATION_QUALITY',
  'SUPPORT_FAMILY_CITO_SPREAD',
])

// ─── Types ────────────────────────────────────────────────────────────────────

/** Single reason-bucket inside a rollup code's secondary breakdown. */
export interface ReasonBreakdownEntry {
  /** The metadata.reason discriminator (e.g. "missing_difficulty") */
  reason: string
  /** Emissions with this reason inside the parent rollup code */
  count: number
  /** Percentage of the parent rollup code's total (0-100) */
  percentOfCode: number
}

export interface CodeFrequencyEntry {
  /** The validator code (e.g. "CONTENT_MISSING_LATEX") */
  code: IssueCode
  /** Total emissions of this code across the batch */
  count: number
  /** Percentage of total findings this code represents (0-100) */
  percentOfTotal: number
  /** Number of unique questions that emitted this code at least once */
  questionsAffected: number
  /** Percentage of all questions emitting this code (0-100) */
  percentOfQuestions: number
  /** Worst severity ever seen for this code in this run */
  maxSeverity: number
  /** Average severity across all emissions */
  avgSeverity: number
  /** Issue kind (blocker / coach / info) — taken from first occurrence */
  kind: IssueKind
  /** Domain (schema / type / graph / content / pedagogy / visual / support / seed / scoring / gate) */
  domain: IssueDomain
  /**
   * Heuristic flag: this code looks like noise.
   * True when kind === 'info' OR severity ≤ TRACE OR percentOfQuestions > 20.
   */
  suspectedNoise: boolean
  /**
   * Secondary breakdown by `metadata.reason` for rollup codes.
   * The full set of rollup codes lives in `ROLLUP_CODES` above —
   * currently CONTENT_CITO_ISSUE, CONTENT_DISTRACTOR_QUALITY,
   * CONTENT_PLUS_INADEQUATE, DIVERSITY_TYPE_OVERUSE,
   * SUPPORT_EXPLANATION_QUALITY, SUPPORT_FAMILY_CITO_SPREAD.
   * Sorted desc by count. Absent for non-rollup codes.
   */
  reasonBreakdown?: ReasonBreakdownEntry[]
}

export interface CodeFrequencyReport {
  generatedAt: string
  totalQuestions: number
  /** Total issue emissions across the batch (matches the "X bevindingen totaal" number). */
  totalIssues: number
  /** Number of unique issue codes encountered. */
  uniqueCodes: number
  /** All codes sorted by count (desc). */
  entries: CodeFrequencyEntry[]
  /** Top N codes by count — convenience slice (default top 10). */
  topCodes: CodeFrequencyEntry[]
  /** Entries flagged as suspectedNoise, sorted by count (desc). */
  suspectedNoise: CodeFrequencyEntry[]
  /** Per-kind breakdown of the total. */
  byKind: Record<IssueKind, number>
  /** Per-domain breakdown of the total. */
  byDomain: Record<IssueDomain, number>
}

// ─── Internal accumulator ─────────────────────────────────────────────────────

interface Acc {
  code: IssueCode
  count: number
  questionsAffected: Set<string>
  severitySum: number
  maxSeverity: number
  kind: IssueKind
  domain: IssueDomain
  /** reason → count, only populated for rollup codes */
  reasonCounts: Map<string, number>
}

// ─── Compute ──────────────────────────────────────────────────────────────────

/**
 * Build a code-frequency report from a PipelineBatchResult.
 *
 * Walks every gate.issues array (not stageResults — gate.issues is the
 * deduplicated, gate-relevant set per question, matching what the UI shows).
 */
export function computeCodeFrequencyReport(
  batchResult: PipelineBatchResult,
  options: { topN?: number; noiseQuestionPctThreshold?: number } = {},
): CodeFrequencyReport {
  const topN = options.topN ?? 10
  const noisePct = options.noiseQuestionPctThreshold ?? 20

  const accByCode = new Map<IssueCode, Acc>()
  const byKind: Record<IssueKind, number> = {
    blocker: 0,
    coach: 0,
    info: 0,
  }
  // Initialize every IssueDomain to 0 — keeps the typed Record exhaustive.
  // P1-3 (mei 2026): 'diversity' is now its own domain (was previously
  // bucketed under 'support' — admin filters showed diversity-issues under
  // the support-tab, which lied about the actual concern).
  // P1-2 (mei 2026): 'familyStats' is now its own domain (family-level
  // content statistics — question count, cito-spread, number-pattern and
  // linguistic-template collapse — were previously emitted under 'support',
  // mixing coverage hygiene with statistical family checks).
  const byDomain: Record<IssueDomain, number> = {
    schema: 0,
    type: 0,
    graph: 0,
    content: 0,
    pedagogy: 0,
    visual: 0,
    support: 0,
    familyStats: 0,
    diversity: 0,
    seed: 0,
    scoring: 0,
    gate: 0,
  }

  let totalIssues = 0
  const totalQuestions = batchResult.totalQuestions

  for (const result of batchResult.results) {
    const qId = result.questionId
    for (const issue of result.gate.issues as ValidationIssue[]) {
      totalIssues += 1
      byKind[issue.kind] = (byKind[issue.kind] ?? 0) + 1
      byDomain[issue.domain] = (byDomain[issue.domain] ?? 0) + 1

      let acc = accByCode.get(issue.code)
      if (!acc) {
        acc = {
          code: issue.code,
          count: 0,
          questionsAffected: new Set<string>(),
          severitySum: 0,
          maxSeverity: 0,
          kind: issue.kind,
          domain: issue.domain,
          reasonCounts: new Map<string, number>(),
        }
        accByCode.set(issue.code, acc)
      }
      acc.count += 1
      acc.questionsAffected.add(qId)
      acc.severitySum += issue.severity
      if (issue.severity > acc.maxSeverity) acc.maxSeverity = issue.severity

      // Track reason discriminator for rollup codes (secondary grouping in UI).
      if (ROLLUP_CODES.has(issue.code)) {
        const reason = (issue.metadata as { reason?: unknown } | undefined)
          ?.reason
        if (typeof reason === 'string' && reason.length > 0) {
          acc.reasonCounts.set(reason, (acc.reasonCounts.get(reason) ?? 0) + 1)
        }
      }
    }
  }

  const safeTotal = Math.max(totalIssues, 1)
  const safeQuestions = Math.max(totalQuestions, 1)

  const entries: CodeFrequencyEntry[] = Array.from(accByCode.values())
    .map((acc) => {
      const percentOfQuestions =
        (acc.questionsAffected.size / safeQuestions) * 100
      const isNoise =
        acc.kind === 'info' ||
        acc.maxSeverity <= SEVERITY.TRACE ||
        percentOfQuestions > noisePct

      let reasonBreakdown: ReasonBreakdownEntry[] | undefined
      if (ROLLUP_CODES.has(acc.code) && acc.reasonCounts.size > 0) {
        const safeCodeTotal = Math.max(acc.count, 1)
        reasonBreakdown = Array.from(acc.reasonCounts.entries())
          .map(([reason, count]) => ({
            reason,
            count,
            percentOfCode: Math.round((count / safeCodeTotal) * 1000) / 10,
          }))
          .sort((a, b) => b.count - a.count)
      }

      return {
        code: acc.code,
        count: acc.count,
        percentOfTotal: Math.round((acc.count / safeTotal) * 1000) / 10,
        questionsAffected: acc.questionsAffected.size,
        percentOfQuestions: Math.round(percentOfQuestions * 10) / 10,
        maxSeverity: acc.maxSeverity,
        avgSeverity: Math.round(acc.severitySum / acc.count),
        kind: acc.kind,
        domain: acc.domain,
        suspectedNoise: isNoise,
        reasonBreakdown,
      }
    })
    .sort((a, b) => b.count - a.count)

  const topCodes = entries.slice(0, topN)
  const suspectedNoise = entries.filter((e) => e.suspectedNoise)

  return {
    generatedAt: new Date().toISOString(),
    totalQuestions,
    totalIssues,
    uniqueCodes: entries.length,
    entries,
    topCodes,
    suspectedNoise,
    byKind,
    byDomain,
  }
}
